import LargeButton from "@general-ui/button/large-button";
import Icon, { COLORS, ICONS } from "@general-ui/icon";
import ImageEditor from "@general-ui/image-editor/image-editor";
import ModalComponent from "@general-ui/modal/modal";
import WaitingIndicator from "@general-ui/waiting-indicator/waiting-indicator";
import { UploadFile } from "connection/uploads";
import { useMemo, useRef, useState } from "react";
import { useTypedSelector } from "store/reducers/use-typed-selector";
import { StaticVideo } from "types/working-model";
import FileInput from "utils/file-input";
import { OptionalComponent } from "utils/optional-component";

interface ThumbnailEditorProps {
	workingVideo: StaticVideo | null;
	onImageChange: (images: { original: string; edited: string }) => void;
}

export default function ThumbnailEditor({ workingVideo, onImageChange }: ThumbnailEditorProps): JSX.Element {

	const { token, user } = useTypedSelector(state => state.AuthReducer);

	const [uploading, setUploading] = useState(false);
	const [editingImage, setEditingImage] = useState(false);
	const [resizedImage, setResizedImage] = useState<File | null>(null);
	
	const replaceImageInput = useRef<HTMLInputElement | null>(null);
	

	async function handleFile(file: File | FileList) {
		if (!user || !token) return;
		const _file = file as File;
		setUploading(true);

		const url = await UploadFile(user, token, _file);
		onImageChange({ 
			original: url,
			edited: '' 
		});

		setEditingImage(true);
		setUploading(false);
	}

	function removeImage() {
		onImageChange({ original: '', edited: '' });
	}
	
	const imageName = useMemo(() => {
		if (workingVideo?.original_image) {
			const parts = workingVideo.original_image.split('/');
			return parts[parts.length - 1];
		} else {
			return '';
		}
	}, [workingVideo?.original_image]);

	async function doneEditingImage() {
		if (!user || !token) return;
		if (resizedImage) {
			setUploading(true);
			const uploaded = await UploadFile(user, token, resizedImage);
			onImageChange({ 
				original: workingVideo?.original_image || '', 
				edited: uploaded 
			});
		}
		setUploading(false);
		setEditingImage(false);
	}

	function pickNewPhoto() {
		replaceImageInput.current?.click();
	}

	async function handleNewPhoto(e: React.ChangeEvent<HTMLInputElement>) {
		const file = e.target.files?.[0];
		if (file) {
			handleFile(file);
		}
	}

  return (
		<>

		{/* upload thumbnail if there isnt an original image, or if there isnt an image the original image is the default thumbnail */}
			<OptionalComponent display={!workingVideo?.original_image}>
				<LargeButton
					title={'Upload Thumbnail Photo'}
					allowedFileTypes={[
						'image/jpeg',
						'image/png',
						'image/gif',
					]}
					subtitle={
						'Allowed filetypes are .jpg, .png, and .gif, and up to 10MB'
					}
					onFile={handleFile}
				/>
			</OptionalComponent>
			<OptionalComponent display={!!workingVideo?.original_image}>
				<div className="video-thumbnail-data">
					<div className="image-container">
						<img
							src={workingVideo?.thumbnail_url}
							alt={workingVideo?.thumbnail_url}
						/>
						<span>{imageName}</span>
					</div>

					<div className="image-actions">
						<button
							className="no-style"
							onClick={() => setEditingImage(true)}
						>
							<Icon
								name={ICONS.EDIT}
								size={16}
								color={COLORS.WHITE}
							/>
						</button>
						<button
							className="no-style"
							onClick={removeImage}
						>
							<Icon
								name={ICONS.TRASH}
								size={16}
								color={COLORS.WHITE}
							/>
						</button>
					</div>
				</div>
			</OptionalComponent>
			<ModalComponent
				open={editingImage}
				onRequestClose={() => setEditingImage(false)}
				cancellable={true}
				closeable={false}
				title='Edit Phtoto'
				footer={
					(<div className="image-edit-footer">
						<div className="image-edit-left">
							{workingVideo?.original_image && (
								<>
									<button
										className="small"
										onClick={pickNewPhoto}
									>
										<Icon
											name={ICONS.UPLOAD}
											size={10}
											color={COLORS.WHITE}
										/>{' '}
										Replace photo
									</button>
									<FileInput
										accept={['image/png', 'image/jpg', 'image/jpeg', 'image/gif']}
										type="file"
										style={{ display: 'none' }}
										ref={replaceImageInput}
										onChange={handleNewPhoto}
									/>
								</>
							)}
						</div>
						<div className="image-edit-right">
							<button
								onClick={() => setEditingImage(false)}
								style={{ marginRight: 16 }}
							>
								Cancel
							</button>
							<button
								onClick={doneEditingImage}
								className="lemonade"
								disabled={uploading}
							>
								{uploading ? (
									<WaitingIndicator />
								) : (
									'Save & Continue'
								)}
							</button>
						</div>
					</div>
			)}>
				{ workingVideo?.original_image && (
				<ImageEditor
					originalImage={workingVideo?.original_image}
					onFinished={(file: File) => setResizedImage(file)}
				/>
				)}
				</ModalComponent>
	</>
	);
}